import Message from "../../pages/components/message";
import { MessageSections } from "../../pages/components/message/message";
import BarekamPie from "./components/pie";
import { CalculatePageContainer } from "../../pages/calculate/styled";
import { ActionBar } from "../../pages/components/action-bar/action-bar";
import React, { useEffect, useState } from "react";
import { TotalCards } from "./components/total-cards/total-cards";
import { DashboardRowContainer } from "./styled";
import BarekamBar from "./components/bar";
import DashboardFilter, {
  DashboardFilterModel,
} from "./components/dashboard-filter";
import ordersService from "../../services/orders.service";
import BarekamText from "../../pages/components/text";
import BarekamGroupedBar from "./components/grouped-bar";
import { GapContainer } from "../../pages/components/card/styled";
import { useAppSelector } from "../../redux/store";

const DashboardPage = () => {
  const [topFilter, setTopFilter] = useState<DashboardFilterModel>({});
  const [deliveredOrdersFilter, setDeliveredOrdersFilter] =
    useState<DashboardFilterModel>({});
  const [pendingOrdersFilter, setPendingOrdersFilter] =
    useState<DashboardFilterModel>({});

  const [totalDeliveredOrders, setTotalDeliveredOrders] = useState(
    [] as ChartData[],
  );
  const [totalOrdersWithStates, setTotalOrdersWithStates] = useState(
    [] as any[],
  );
  const [totalSubmittedPayments, setTotalSubmittedPayments] = useState(
    [] as ChartData[],
  );
  const [upcomingPayments, setUpcomingPayments] = useState([] as ChartData[]);

  const { languageId } = useAppSelector((state) => state.messagesReducer);

  useEffect(() => {
    const totalDeliveredData =
      ordersService.getTotalOrdersDataGroupedByCountries({
        ...deliveredOrdersFilter,
        state: 5,
      });
    const totalPaidData = ordersService.getTotalOrdersDataGroupedByCountries({
      ...deliveredOrdersFilter,
      paymentStatus: 2,
    });
    const upcomingPaymentData =
      ordersService.getTotalOrdersDataGroupedByCountries({
        ...deliveredOrdersFilter,
        paymentStatus: { $not: 2 },
      });

    totalDeliveredData.then((response) => {
      setTotalDeliveredOrders(() =>
        response.result.map((result: any) => {
          const countryName = result["_id"]?.country[languageId] || "";

          return { name: countryName, value: result.totalOrders };
        }),
      );
    });
    totalPaidData.then((response) => {
      setTotalSubmittedPayments(() =>
        response.result.map((result: any) => {
          const countryName = result["_id"]?.country[languageId] || "";

          return { name: countryName, value: result.totalAmount };
        }),
      );
    });
    upcomingPaymentData.then((response) => {
      setUpcomingPayments(() =>
        response.result.map((result: any) => {
          const countryName = result["_id"]?.country[languageId] || "";

          return { name: countryName, value: result.totalAmount };
        }),
      );
    });
  }, [deliveredOrdersFilter]);

  useEffect(() => {
    const totalDataWithStates =
      ordersService.getTotalOrdersDataGroupedByCountries(
        { ...pendingOrdersFilter },
        true,
      );

    totalDataWithStates.then((response) => {
      const entries = new Map<string, any>();

      response.result.forEach(
        (result: {
          totalOrders: number;
          _id: { country: string; state: string };
        }) => {
          if (!entries.has(result._id.country)) {
            entries.set(result._id.country, []);
          }

          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          entries.get(result._id.country)[result._id.state] =
            result.totalOrders;
        },
      );

      const orderData = [] as any[];

      entries.forEach((value, name) => {
        const countryName = name[languageId];

        orderData.push({ name: countryName, ...value });
      });

      setTotalOrdersWithStates(orderData);
    });
  }, [pendingOrdersFilter]);

  function handleTopFilterChange(filter: DashboardFilterModel) {
    setTopFilter(filter);
  }

  function handleDeliveredFilterChange(filter: DashboardFilterModel) {
    setDeliveredOrdersFilter(filter);
  }

  function handlePendingFilterChange(filter: DashboardFilterModel) {
    setPendingOrdersFilter(filter);
  }

  return (
    <CalculatePageContainer>
      <ActionBar
        headerText={
          <Message
            messageKey={"dashboard_page_title"}
            sectionId={MessageSections.ORDERS}
          />
        }
        customComponent={
          <DashboardFilter
            onFilterChange={handleTopFilterChange}
            filter={topFilter}
            withCountries
            withDate
          />
        }
      />

      <DashboardRowContainer>
        <TotalCards filter={topFilter} />
      </DashboardRowContainer>

      <DashboardRowContainer style={{ padding: "1rem" }}>
        <BarekamText variant={"l"} bold>
          <Message
            messageKey={"delivered_orders"}
            sectionId={MessageSections.ORDERS}
          />
        </BarekamText>

        <div style={{ width: "32%", display: "flex", alignItems: "center" }}>
          <DashboardFilter
            onFilterChange={handleDeliveredFilterChange}
            filter={deliveredOrdersFilter}
            withDate
          />
        </div>
      </DashboardRowContainer>

      <DashboardRowContainer>
        <BarekamPie
          title={"total_delivered_by_country"}
          data={totalDeliveredOrders}
        />
        <BarekamPie
          title={"total_submitted_payments_by_country"}
          data={totalSubmittedPayments}
        />
      </DashboardRowContainer>

      <DashboardRowContainer>
        <BarekamBar
          title={"total_delivered_by_country"}
          data={totalDeliveredOrders}
        />
        <BarekamBar
          title={"total_submitted_payments_by_country"}
          data={totalSubmittedPayments}
        />
      </DashboardRowContainer>

      <DashboardRowContainer style={{ padding: "1rem" }}>
        <BarekamText variant={"l"} bold>
          <Message
            messageKey={"pending_orders"}
            sectionId={MessageSections.ORDERS}
          />
        </BarekamText>
      </DashboardRowContainer>

      <DashboardRowContainer>
        <BarekamGroupedBar
          title={"total_orders_by_states"}
          filters={
            <GapContainer style={{ width: " 100%" }}>
              <DashboardFilter
                onFilterChange={handlePendingFilterChange}
                filter={pendingOrdersFilter}
                withState
                withCountries
              />
            </GapContainer>
          }
          data={totalOrdersWithStates}
        />
        <BarekamBar
          title={"upcoming_payments_by_country"}
          data={upcomingPayments}
          extended
        />
      </DashboardRowContainer>
    </CalculatePageContainer>
  );
};

export interface ChartData {
  name: string;
  value: number;
  state?: string;
}

export default DashboardPage;
